import { create } from 'zustand';
import { AIService } from 'src/services/ai';
import {
  EstimationRequest,
  EstimationResponse,
  InsuranceChatRequest,
  InsuranceChatResponse,
  InsuranceChatHook,
} from 'src/services/interfaces/ai';
import { useApplicationStore } from 'src/store/application';
import { useInsuranceStore } from 'src/store/insurance';
import { useGeneralStore } from 'src/store';
import { encodeJSONToBase64 } from '@utils/base64';
import axios from 'axios';
import {
  chatWebSocket,
  WebSocketMessage,
} from 'src/services/websocket';

const isWebSocketMode = () =>
  import.meta.env.VITE_CHAT_TYPE === 'websocket';

interface AIState {
  // Estimation state
  estimationLoading: boolean;
  estimationResult: EstimationResponse | null;
  estimationError: string | null;

  chatLoading: boolean;
  chatSessionId: string | null;
  chatHistory: InsuranceChatResponse[];
  chatError: string | null;
  currentPage: string | null;

  // WebSocket streaming state
  isStreaming: boolean;
  streamingAnswer: string;
  wsConnected: boolean;
  _currentMessage: string | null;
  _wsCallbacksRegistered: boolean;
  _connectingForSend: boolean;

  // Plan change tracking
  isChangeAfterEstimation: boolean;

  // Actions
  runEstimation: (data: EstimationRequest) => Promise<void>;
  sendChatMessage: (
    data: InsuranceChatRequest,
    application: any,
    page_context?: string | null
  ) => Promise<void>;
  connectWebSocket: (
    userId?: string,
    groupHash?: string
  ) => Promise<void>;
  disconnectWebSocket: () => void;
  generateNewSessionId: () => void;
  clearEstimation: () => void;
  clearChat: () => void;
  clearErrors: () => void;
  setCurrentPage: (page: string) => void;
  setChangeAfterEstimation: (changed: boolean) => void;
}

// Helper to get group hash
const getGroupHash = (): string | undefined => {
  if (typeof window === 'undefined') return undefined;
  return (
    import.meta.env.VITE_APP_GROUP_HASH ||
    document
      .querySelector('meta[name="x-group-hash"]')
      ?.getAttribute('content') ||
    undefined
  );
};


export const useAIStore = create<AIState>((set, get) => ({
  estimationLoading: false,
  estimationResult: null,
  estimationError: null,

  chatLoading: false,
  chatSessionId: null,
  chatHistory: [],
  chatError: null,
  currentPage: null,

  isChangeAfterEstimation: false,

  // WebSocket streaming state
  isStreaming: false,
  streamingAnswer: '',
  wsConnected: false,
  _currentMessage: null,

  // Actions
  _wsCallbacksRegistered: false,
  _connectingForSend: false,

  connectWebSocket: async (
    userId?: string,
    groupHash?: string
  ) => {
    const currentState = get();
    if (currentState.wsConnected && chatWebSocket.isConnected()) {
      return;
    }

    const resolvedGroupHash = groupHash || getGroupHash();

    // Register callbacks only once to prevent duplicates on reconnect
    if (!get()._wsCallbacksRegistered) {
      set({ _wsCallbacksRegistered: true });

    chatWebSocket.onChunk((content: string) => {
      set((state) => ({
        streamingAnswer: state.streamingAnswer + content,
        isStreaming: true,
      }));
    });

    chatWebSocket.onEnd((hooks: InsuranceChatHook[]) => {
      const state = get();

      // Update the last chat history entry (user message) with the AI answer
      const updatedHistory = [...state.chatHistory];
      if (updatedHistory.length > 0) {
        const lastItem = updatedHistory[updatedHistory.length - 1];
        updatedHistory[updatedHistory.length - 1] = {
          ...lastItem,
          answer: state.streamingAnswer,
          hooks,
        };
      }

      set({
        chatHistory: updatedHistory,
        chatLoading: false,
        isStreaming: false,
        streamingAnswer: '',
        _currentMessage: null,
      });

      if (hooks && hooks.length > 0) {
        window.dispatchEvent(
          new CustomEvent('ai-hooks', {
            detail: { hooks },
          })
        );
      }
    });

    chatWebSocket.onError((error: string) => {
      console.error('[AI Store] WebSocket error:', error);
      const state = get();

      // Find the last user message without an answer and set error as inline response
      const updatedHistory = [...state.chatHistory];
      const lastIdx = updatedHistory.length - 1;
      if (lastIdx >= 0 && !updatedHistory[lastIdx].answer) {
        updatedHistory[lastIdx] = {
          ...updatedHistory[lastIdx],
          answer: error || 'Request timeout. The server is taking too long to respond. Please try again.',
        };
      }

      set({
        chatHistory: updatedHistory,
        chatError: null,
        chatLoading: false,
        isStreaming: false,
        streamingAnswer: '',
        _currentMessage: null,
      });
    });

    chatWebSocket.onClose(() => {
      set({ wsConnected: false });
      // If chatLoading is still true, the error callback will handle it
      // (either from streaming timeout or max retries exhausted)
    });

    chatWebSocket.onOpen(() => {
      const state = get();
      set({ wsConnected: true });

      // If we reconnected but there's still a pending message, it was lost
      // Server doesn't know about it after reconnect — show error inline
      // Skip this check when sendChatMessage is connecting — it will send
      // the message itself right after connect resolves.
      if (state.chatLoading && state._currentMessage && !state._connectingForSend) {
        const updatedHistory = [...state.chatHistory];
        const lastIdx = updatedHistory.length - 1;
        if (lastIdx >= 0 && !updatedHistory[lastIdx].answer) {
          updatedHistory[lastIdx] = {
            ...updatedHistory[lastIdx],
            answer: 'Connection was lost. Please try sending your message again.',
          };
        }
        set({
          chatHistory: updatedHistory,
          chatLoading: false,
          isStreaming: false,
          streamingAnswer: '',
          _currentMessage: null,
        });
      }
    });

    } // end of _wsCallbacksRegistered check

    try {
      await chatWebSocket.connect(
        userId,
        resolvedGroupHash,
        currentState.chatSessionId || undefined
      );
      set({ wsConnected: true });
    } catch (error) {
      console.error(
        '[AI Store] Failed to connect WebSocket:',
        error
      );
      set({ wsConnected: false });
      throw error;
    }
  },

  disconnectWebSocket: () => {
    chatWebSocket.disconnect();
    set({ wsConnected: false });
  },

  runEstimation: async (data: EstimationRequest) => {
    set({ estimationLoading: true, estimationError: null });

    try {
      const result = await AIService.runEstimation(data);
      set({
        estimationResult: result,
        estimationLoading: false,
      });
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to run estimation';
      set({
        estimationError: errorMessage,
        estimationLoading: false,
      });
    }
  },

  sendChatMessage: async (
    data: InsuranceChatRequest,
    application: any,
    page_context?: string | null
  ) => {
    const currentState = get();

    const sessionId =
      data.session_id ||
      currentState.chatSessionId ||
      crypto.randomUUID();

    if (!currentState.chatSessionId) {
      set({ chatSessionId: sessionId });
    }

    // Offline check — works for both WS and HTTP modes
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      const offlineItem: InsuranceChatResponse = {
        session_id: sessionId,
        message: data.message,
        answer: 'You appear to be offline. Please check your internet connection and try again.',
        sources: [],
      };
      set((s) => ({
        chatHistory: [...s.chatHistory, offlineItem],
        chatError: null,
        chatLoading: false,
      }));
      return;
    }

    // Shared context building
    const applicationForChat = application
      ? {
          ...application,
          phq: application.phq
            ? {
                ...application.phq,
                signature: application.phq.signature ? true : null,
              }
            : application.phq,
        }
      : application;

    const applicationDTO = encodeJSONToBase64(applicationForChat);
    const {
      activeCategories,
      acceptedTier,
      signature,
      signedAt,
      hasCompletedApplication,
      isMainPlanRejected,
      phqAcceptedAt,
      activeSubscriptionPlans,
      progress,
      abandonedAt,
      updatedAt,
    } = useApplicationStore.getState();
    const plans = useInsuranceStore.getState().plans;
    const siteConfig = useGeneralStore.getState().siteConfig;

    const canHook = !!acceptedTier && !signature;

    const hasActiveMedicalPlan = (() => {
      if (
        !activeSubscriptionPlans ||
        activeSubscriptionPlans.length === 0
      )
        return false;
      const activePlanIds = activeSubscriptionPlans.map(
        (p) => p.planId
      );
      return plans.some(
        (plan) =>
          activePlanIds.includes(plan.id) &&
          plan.category === 'Major Medical'
      );
    })();

    const isPHQEnabled =
      siteConfig?.phqEnabled !== false &&
      !acceptedTier &&
      !isMainPlanRejected &&
      !phqAcceptedAt &&
      !hasActiveMedicalPlan;

    // ─── WebSocket mode ───
    if (isWebSocketMode()) {
      console.log('[AI Store] WS mode — onLine:', navigator?.onLine, 'wsConnected:', currentState.wsConnected, 'ws.isConnected:', chatWebSocket.isConnected());

      // Check network availability first
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        console.log('[AI Store] Offline detected — showing inline error');
        const offlineItem: InsuranceChatResponse = {
          session_id: sessionId,
          message: data.message,
          answer: 'You appear to be offline. Please check your internet connection and try again.',
          sources: [],
        };
        set((s) => ({
          chatHistory: [...s.chatHistory, offlineItem],
          chatError: null,
          chatLoading: false,
        }));
        return;
      }

      // Add user message to chat history immediately — before connect attempt
      const userMessageItem: InsuranceChatResponse = {
        session_id: sessionId,
        message: data.message,
        answer: '',
        sources: [],
      };

      set((s) => ({
        chatLoading: true,
        chatError: null,
        isStreaming: false,
        streamingAnswer: '',
        _currentMessage: data.message,
        chatHistory: [...s.chatHistory, userMessageItem],
      }));

      // Ensure connected
      if (
        !currentState.wsConnected ||
        !chatWebSocket.isConnected()
      ) {
        try {
          set({ _connectingForSend: true });
          await get().connectWebSocket(
            data.userId?.toString(),
            getGroupHash()
          );
          set({ _connectingForSend: false });
        } catch {
          set({ _connectingForSend: false });
          // Show connection error inline — update last history entry
          const state = get();
          const updatedHistory = [...state.chatHistory];
          const lastIdx = updatedHistory.length - 1;
          if (lastIdx >= 0 && !updatedHistory[lastIdx].answer) {
            updatedHistory[lastIdx] = {
              ...updatedHistory[lastIdx],
              answer: 'Unable to connect to the chat server. Please check your connection and try again.',
            };
          }
          set({
            chatHistory: updatedHistory,
            chatError: null,
            chatLoading: false,
            _currentMessage: null,
          });
          return;
        }
      }

      const wsContext: WebSocketMessage['context'] =
        currentState.currentPage
          ? {
              page: currentState.currentPage,
              ...(applicationDTO ? { applicationDTO } : {}),
              ...(page_context ? { page_context } : {}),
              ...(activeCategories && activeCategories.length > 0
                ? { activeCategories }
                : {}),
              isPHQEnabled,
              signedAt: signedAt ?? null,
              hasCompletedApplication:
                hasCompletedApplication ?? false,
              progress,
              abandonedAt,
              updatedAt,
            }
          : undefined;

      const wsMessage: WebSocketMessage = {
        message: data.message,
        context: wsContext,
        can_hooks: canHook || undefined,
      };

      chatWebSocket.sendMessage(wsMessage);
      return;
    }

    // ─── HTTP mode (default) ───
    const userMessageItem: InsuranceChatResponse = {
      session_id: sessionId,
      message: data.message,
      answer: '',
      sources: [],
    };

    set({
      chatLoading: true,
      chatError: null,
      chatHistory: [...currentState.chatHistory, userMessageItem],
    });

    const context = currentState.currentPage
      ? {
          page: currentState.currentPage,
          ...(applicationDTO ? { applicationDTO } : {}),
          ...(page_context ? { page_context } : {}),
          ...(activeCategories && activeCategories.length > 0
            ? { activeCategories }
            : {}),
          signedAt: signedAt ?? null,
          hasCompletedApplication:
            hasCompletedApplication ?? false,
          progress,
          abandonedAt,
          updatedAt,
        }
      : undefined;

    const chatRequest: InsuranceChatRequest = {
      ...data,
      session_id: sessionId,
      context,
      canHook: canHook || undefined,
    };

    const REQUEST_TIMEOUT_MS = 40000;
    let requestTimeoutId: ReturnType<typeof setTimeout> | null =
      null;

    const requestTimeoutPromise = new Promise<never>(
      (_, reject) => {
        requestTimeoutId = setTimeout(() => {
          reject(new Error('Request timeout'));
        }, REQUEST_TIMEOUT_MS);
      }
    );

    try {
      const requestPromise = AIService.sendInsuranceChatMessage(
        chatRequest,
        isPHQEnabled
      )
        .then((response) => {
          if (requestTimeoutId) {
            clearTimeout(requestTimeoutId);
            requestTimeoutId = null;
          }
          return response;
        })
        .catch((error) => {
          if (requestTimeoutId) {
            clearTimeout(requestTimeoutId);
            requestTimeoutId = null;
          }
          throw error;
        });

      const response = await Promise.race([
        requestPromise,
        requestTimeoutPromise,
      ]);

      if (
        response.session_id &&
        response.session_id !== sessionId
      ) {
        set({ chatSessionId: response.session_id });
      }

      set((state) => {
        const newHistory = [...state.chatHistory];
        const lastIndex = newHistory.length - 1;

        if (
          lastIndex >= 0 &&
          newHistory[lastIndex].message === data.message &&
          !newHistory[lastIndex].answer
        ) {
          newHistory[lastIndex] = {
            ...response,
            message: response.message || data.message,
            session_id: response.session_id || sessionId,
          };
        } else {
          newHistory.push(response);
        }

        return {
          chatHistory: newHistory,
          chatLoading: false,
          chatError: null,
        };
      });

      if (response.hooks && response.hooks.length > 0) {
        window.dispatchEvent(
          new CustomEvent('ai-hooks', {
            detail: { hooks: response.hooks },
          })
        );
      }
    } catch (error: unknown) {
      if (requestTimeoutId) {
        clearTimeout(requestTimeoutId);
        requestTimeoutId = null;
      }

      let errorMessage =
        'Failed to send message. Please try again.';

      if (axios.isAxiosError(error)) {
        if (
          error.code === 'ERR_NETWORK' ||
          error.code === 'ERR_INTERNET_DISCONNECTED'
        ) {
          errorMessage =
            'No internet connection. Please check your network and try again.';
        } else if (
          error.code === 'ECONNABORTED' ||
          error.message.includes('timeout')
        ) {
          errorMessage =
            'Request timeout. The server is taking too long to respond. Please try again.';
        } else if (error.response) {
          if (error.response.status >= 500) {
            errorMessage =
              "We're experiencing a technical issue. Please try again later.";
          } else {
            errorMessage =
              error.response.data?.message ||
              `Server error: ${error.response.status}`;
          }
        } else {
          errorMessage =
            error.message ||
            'Network error. Please check your connection and try again.';
        }
      } else if (error instanceof Error) {
        if (error.message === 'Request timeout') {
          errorMessage =
            'Request timeout. The server is taking too long to respond. Please try again.';
        } else {
          errorMessage = error.message;
        }
      }

      set((state) => {
        const newHistory = [...state.chatHistory];

        let foundIndex = -1;
        for (let i = newHistory.length - 1; i >= 0; i--) {
          if (
            newHistory[i].message === data.message &&
            !newHistory[i].answer
          ) {
            foundIndex = i;
            break;
          }
        }

        if (foundIndex >= 0) {
          newHistory[foundIndex] = {
            ...newHistory[foundIndex],
            answer: errorMessage,
          };
        } else {
          newHistory.push({
            session_id: sessionId,
            message: data.message,
            answer: errorMessage,
            sources: [],
          });
        }

        return {
          chatHistory: newHistory,
          chatError: errorMessage,
          chatLoading: false,
        };
      });
    }
  },

  generateNewSessionId: () => {
    const newSessionId = crypto.randomUUID();
    set({ chatSessionId: newSessionId });
  },

  clearEstimation: () => {
    set({
      estimationResult: null,
      estimationError: null,
    });
  },

  clearChat: () => {
    set({
      chatHistory: [],
      chatSessionId: null,
      chatError: null,
      streamingAnswer: '',
      isStreaming: false,
      _currentMessage: null,
    });
  },

  clearErrors: () => {
    set({
      estimationError: null,
      chatError: null,
    });
  },

  setCurrentPage: (page: string) => {
    set({ currentPage: page });
  },

  setChangeAfterEstimation: (changed: boolean) => {
    set({ isChangeAfterEstimation: changed });
  },
}));
